/**
 * Yes, this file is one giant exception to the rules, it is intentional.
 * If we were to do this with Javascript it would be way less performant
 * And more error prone
 */

/* eslint-disable @atlaskit/ui-styling-standard/no-classname-prop */
/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-selectors */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */

/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type FC, useEffect, useState } from 'react';

// eslint-disable-next-line no-unused-vars
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';

import { NEW_ACCESS_REQUEST_INDICATOR_STATE_INSTANCE } from './NewAccessRequestIndicatorState.tsx';

const styles = cssMap({
	accessRequestsStatusContainer: { position: 'relative' },
	accessRequestsStatusContainerInline: { display: 'inline-block' },
	accessRequestsStatusIndicatorOutline: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '10px',
		height: '10px',
		position: 'absolute',
		borderRadius: token('radius.medium'),
		zIndex: 1,
	},
	surfaceBackground: {
		'#unseen-request-indicator': {
			transition: 'transform 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
			transformOrigin: 'center',
			backgroundColor: token('elevation.surface'),
		},
		'&:hover': {
			// @ts-ignore
			'#unseen-request-indicator': {
				// This works better than elevation.surface.hovered ... for now
				backgroundColor: token('elevation.surface.raised.hovered'),
			},
		},
		'&:active': {
			// @ts-ignore
			'#unseen-request-indicator': {
				backgroundColor: token('elevation.surface.pressed'),
			},
		},
	},
	overlayBackground: {
		'#unseen-request-indicator': {
			backgroundColor: token('elevation.surface.overlay'),
		},
		'&:hover': {
			// @ts-ignore
			'#unseen-request-indicator': {
				backgroundColor: token('elevation.surface.overlay.hovered'),
			},
		},
		'&:active': {
			// @ts-ignore
			'#unseen-request-indicator': {
				backgroundColor: token('elevation.surface.overlay.pressed'),
			},
		},
	},
	accessRequestsStatusIndicator: {
		// @ts-ignore
		backgroundColor: token('color.icon.information'),
		width: '6px',
		height: '6px',
		borderRadius: token('radius.medium'),
	},
});

interface NewAccessRequestIndicatorProps {
	children: React.ReactNode;
	top: number;
	right: number;
	source: string;
	isInsideOverlay?: boolean;
	/**
	 * The HTML element to render as the container. Defaults to 'div'.
	 * Use 'span' when the indicator is rendered inside a context that requires inline-level elements,
	 * such as inside an @atlaskit/tabs Tab component where -webkit-line-clamp is applied.
	 * See: https://product-fabric.atlassian.net/browse/CCPERMS-5807
	 */
	as?: 'div' | 'span';
}

export const NewAccessRequestIndicatorInternal: FC<NewAccessRequestIndicatorProps> = ({
	children,
	top,
	right,
	source,
	isInsideOverlay = false,
	as: Container = 'div',
}) => {
	const [showIndicator, setShowIndicator] = useState(false);
	const transform = `scale(${showIndicator ? 1 : 0})`;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		return NEW_ACCESS_REQUEST_INDICATOR_STATE_INSTANCE.subscribe(setShowIndicator);
	}, []);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: showIndicator ? 'shown' : 'hidden',
				actionSubject: 'pendingRequestIndicator',
				attributes: { isInsideOverlay },
				source,
			},
		}).fire();
	}, [showIndicator, createAnalyticsEvent, isInsideOverlay, source]);

	return (
		<Container
			css={[
				styles.accessRequestsStatusContainer,
				Container === 'span' && styles.accessRequestsStatusContainerInline,
				isInsideOverlay ? styles.overlayBackground : styles.surfaceBackground,
			]}
		>
			<div
				id="unseen-request-indicator"
				data-testid="unseen-request-indicator"
				css={[
					styles.accessRequestsStatusIndicatorOutline,
					showIndicator && styles.surfaceBackgroundVisible,
				]}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={{ top, right, transform }}
				aria-hidden
			>
				<div css={styles.accessRequestsStatusIndicator} />
			</div>

			{children}
		</Container>
	);
};

export const NewAccessRequestIndicator: FC<NewAccessRequestIndicatorProps> = (props) =>
	// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
	UNSAFE_expValEqualsNoExposure('cc_fe_realtime_access_requests', 'isEnabled', true) ? (
		<NewAccessRequestIndicatorInternal {...props} />
	) : (
		props.children
	);
